import React, { useEffect, createContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import firebase from "firebase/compat/app";
import { CircularProgress, Stack, Typography } from "@mui/material";
import SignInScreen from "./SignInScreen";
import { useGet } from "../request";

const UserContext = createContext();

function gtag() {
  window.dataLayer.push(arguments);
}

const usePageTracking = () => {
  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      const GA_TRACKING_ID = process.env.REACT_APP_MEASUREMENT_ID;

      // Load the Google Analytics script
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
      script.async = true;
      document.body.appendChild(script);

      // Initialize Google Analytics
      window.dataLayer = window.dataLayer || [];
      gtag("js", new Date());
      gtag("config", GA_TRACKING_ID, {
        page_path: window.location.pathname,
        // Deny ad_storage and analytics_storage by default
        ad_storage: "denied",
        analytics_storage: "denied",
      });
    }
  }, []);
};

const AuthGuard = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Try to load cached user from localStorage immediately
    try {
      const cached = localStorage.getItem('user_cache');
      return cached ? JSON.parse(cached) : undefined;
    } catch (e) {
      return undefined;
    }
  });
  const lastProcessedUidRef = useRef(null);

  const [userLoading, setUserLoading] = useState(() => {
    // Only show loading if we don't have cached user data
    try {
      const cached = localStorage.getItem('user_cache');
      return !cached;
    } catch (e) {
      return true;
    }
  });
  const get = useGet();

  usePageTracking();

  const resetUserState = () => {
    setUser(null);
    setUserLoading(false);
  };

  useEffect(() => {
    // Absorb any pending redirect sign-in result to avoid SDK re-triggers
    firebase
      .auth()
      .getRedirectResult()
      .catch(() => {});

    const unsubscribe = firebase.auth().onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // Avoid re-processing the same user repeatedly (prevents loops)
        if (lastProcessedUidRef.current === authUser.uid && user) {
          return;
        }
        lastProcessedUidRef.current = authUser.uid;
        setUserLoading(true);
        try {
          // Use cached token; let SDK refresh in background as needed
          await authUser.getIdToken();
          const response = await get("users/profile");

          if (response && response.ok !== false && !response.error) {
            const userData = {
              ...response,
              email: authUser?.email ?? response.email,
              name: authUser?.displayName ?? response.name,
              phone: authUser?.phoneNumber ?? response.phone,
            };
            setUser(userData);
            
            // Cache user data in localStorage
            try {
              localStorage.setItem('user_cache', JSON.stringify(userData));
            } catch (e) {
              console.error('Failed to cache user data:', e);
            }
          } else {
            // Avoid login loops: fall back to minimal user; app can still render
            const userData = {
              id: authUser.uid,
              email: authUser.email,
              name: authUser.displayName,
              role: "user",
            };
            setUser(userData);
            
            // Cache minimal user data
            try {
              localStorage.setItem('user_cache', JSON.stringify(userData));
            } catch (e) {
              console.error('Failed to cache user data:', e);
            }
          }
        } catch (error) {
          // Avoid login loops in case of transient failures
          const userData = {
            id: authUser.uid,
            email: authUser.email,
            name: authUser.displayName,
            role: "user",
          };
          setUser(userData);
          
          // Cache minimal user data even on error
          try {
            localStorage.setItem('user_cache', JSON.stringify(userData));
          } catch (e) {
            console.error('Failed to cache user data:', e);
          }
        } finally {
          setUserLoading(false);
        }
      } else {
        lastProcessedUidRef.current = null;
        // Clear cached user data when logged out
        try {
          localStorage.removeItem('user_cache');
        } catch (e) {
          console.error('Failed to clear user cache:', e);
        }
        resetUserState();
      }
    });

    return unsubscribe;
  }, [get, user]);

  const renderProviderChildren = () => {
    if (userLoading) {
      return (
        <Stack
          height={"100vh"}
          width={"100vw"}
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Stack>
      );
    }

    if (user) {
      if (user.role === "banned") {
        return (
          <Stack height={"100vh"} width={"100vw"} alignItems="center" justifyContent="center">
            <Typography variant="h5">Your account has been banned. Please contact support.</Typography>
          </Stack>
        );
      }
      return (
        <UserContext.Provider value={[user, setUser]}>
          {children}
        </UserContext.Provider>
      );
    }

    return (
      <UserContext.Provider value={[user, setUser]}>
        <SignInScreen />{" "}
      </UserContext.Provider>
    );
  };

  return renderProviderChildren();
};

AuthGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthGuard;

export { UserContext };
